// Menu grade-band gate — the ONE matching rule for "may this grade band
// (GradeRange code) order from this menu?".
//
// Menus/templates tag the bands they serve as `gradeRangeCodes` (the chip
// vocabulary in menu-template-title.ts); menu INSTANCES carry the tags inside
// `resolutionContext`, falling back to `settings` for directly-authored menus.
// The family ordering surface has always filtered whole menus by the
// student's band with these semantics; the school order sheet's per-age-group
// column gate and its server-side submit safety net reuse the same rule so a
// menu is orderable for exactly the same bands on every surface:
//
//   - EMPTY/missing menu codes → the menu is untagged → matches EVERY band.
//     (Back-compat: menus created before grade tagging keep working
//     everywhere; required chips + the 2026-07-20 backfill make this rare.)
//   - Unknown band (null/empty code) → BLOCKED (2026-07 fail-closed flip).
//     USDA compliance: a grade we can't place in a band gets NO menus.
//     Subjects that are band-less BY DESIGN (whole-school column, group
//     columns with no resolved tier) skip the gate via `hasGradeRangeCode`.
//   - Otherwise → normalized membership test against the menu's codes.
//
// Codes normalize exactly like menu-template-title's span builder
// (lowercase, underscores → dashes, legacy "age-3-5" → "pk3-5") so legacy
// rows keep matching their renamed canonical codes.

/**
 * Canonicalize a grade-range code: lowercase, underscores → dashes, and the
 * pre-rename "age-3-5" code maps to its successor "pk3-5". Shared by the
 * template title span builder and the menu grade-band gate.
 */
export function normalizeGradeRangeCode(code: string): string {
  const normalized = code.trim().toLowerCase().replace(/_/gu, '-')
  return normalized === 'age-3-5' ? 'pk3-5' : normalized
}

/**
 * A COMBINED band expands to its constituent seeded bands: ["k-8"] spans the
 * same grades as ["k-5", "6-8"], so span labels collapse correctly ("K-8";
 * ["k-8", "9-12"] renders "K-12") and the grade gate matches a K-8 subject
 * against a menu tagged with either constituent band (and vice versa).
 * Callers pass NORMALIZED codes (see normalizeGradeRangeCode).
 */
export function expandCombinedGradeRangeCode(code: string): string[] {
  if (code === 'k-8') {
    return ['k-5', '6-8']
  }
  // The locked PK ordering band (portion-groups.ts) spans both preschool
  // menu tags, exactly as k-8 spans its constituents.
  // The childcare Age 1-5 ordering band spans the same two menu tags.
  if (code === 'pk' || code === 'age-1-5') {
    return ['age-1-2', 'pk3-5']
  }
  // The K-12 breakfast-partition band spans every K-12 pattern band. PK is
  // deliberately NOT a constituent — PK stays its own column per meal-
  // pattern needs, so a K-12 column must never enable PK-tagged menus.
  if (code === 'k-12') {
    return ['k-5', '6-8', '9-12']
  }
  return [code]
}

function readStringArrayField(value: unknown, key: string): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return []
  }
  const raw = (value as Record<string, unknown>)[key]
  if (!Array.isArray(raw)) {
    return []
  }
  return raw.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

/**
 * A menu's grade-range tags: `resolutionContext.gradeRangeCodes` (template
 * instances) first, `settings.gradeRangeCodes` (directly-authored menus) as
 * the fallback. `[]` = untagged. Raw codes are returned un-normalized —
 * `menuAllowsGradeRangeCode` normalizes at match time.
 */
export function readMenuGradeRangeCodes(resolutionContext: unknown, settings: unknown): string[] {
  const fromResolutionContext = readStringArrayField(resolutionContext, 'gradeRangeCodes')
  if (fromResolutionContext.length > 0) {
    return fromResolutionContext
  }
  return readStringArrayField(settings, 'gradeRangeCodes')
}

/**
 * True when a menu tagged with `menuGradeRangeCodes` serves the grade band
 * `gradeRangeCode`. Empty/missing menu codes match every band (legacy
 * back-compat — pre-tagging menus; the 2026-07-20 backfill + required chips
 * shrink this population to ~zero). An unknown band (null/empty code) is
 * BLOCKED — fail-closed USDA compliance: a menu not designated for a grade
 * band cannot be served to it, and a grade the system can't place in a band
 * gets no menus. Call sites whose subject is band-less BY DESIGN (the
 * whole-school order column, group columns with no resolved tier) must skip
 * the gate explicitly via `hasGradeRangeCode` — grep for it to audit every
 * sanctioned fail-open.
 */
export function menuAllowsGradeRangeCode(
  menuGradeRangeCodes: readonly string[] | null | undefined,
  gradeRangeCode: string | null | undefined,
): boolean {
  if (!hasGradeRangeCode(gradeRangeCode)) {
    return false
  }
  const menuCodes = (menuGradeRangeCodes ?? []).filter((code) => code.trim() !== '')
  if (menuCodes.length === 0) {
    return true
  }
  // Combined bands (k-8) match through their constituent seeded bands in
  // BOTH directions: a K-8 subject may order from a menu tagged k-5 or 6-8,
  // and a menu tagged k-8 serves k-5 and 6-8 subjects.
  const bandCodes = new Set(expandCombinedGradeRangeCode(normalizeGradeRangeCode(gradeRangeCode)))
  return menuCodes
    .flatMap((code) => expandCombinedGradeRangeCode(normalizeGradeRangeCode(code)))
    .some((code) => bandCodes.has(code))
}

/**
 * The sanctioned escape hatch for the fail-closed gate: subjects that are
 * band-less BY DESIGN (whole-school columns, unresolved group columns) check
 * this and skip the gate; a student/grade subject without a band must NOT —
 * they are blocked.
 */
export function hasGradeRangeCode(code: string | null | undefined): code is string {
  return typeof code === 'string' && code.trim() !== ''
}
